import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import {
  CustomerRecordItem,
  CustomerRecordKind,
  CustomerRow,
  listCustomerRecords,
} from "../../api/platformCustomers";
import { UI_LOADING } from "../../constants/uiLabels";
import "./CustomerRecordsModal.css";

type Props = {
  customer: CustomerRow;
  initialKind: CustomerRecordKind;
  onClose: () => void;
};

const TABS: Array<{ kind: CustomerRecordKind; label: string; countKey: keyof CustomerRow }> = [
  { kind: "students", label: "Kursiyer", countKey: "total_students" },
  { kind: "instructors", label: "Eğitmen", countKey: "total_instructors" },
  { kind: "vehicles", label: "Araç", countKey: "total_vehicles" },
  { kind: "users", label: "Kullanıcı", countKey: "total_users" },
];

function statusLabel(kind: CustomerRecordKind, status: string | null): string {
  if (!status) return "—";
  if (kind === "users") {
    const map: Record<string, string> = {
      tenant_admin: "Kurum Yöneticisi",
      manager: "Şube Müdürü",
      office_staff: "Büro Personeli",
      instructor: "Eğitmen",
      student: "Kursiyer",
    };
    return map[status] ?? status;
  }
  if (kind === "students") {
    const map: Record<string, string> = {
      active: "Aktif",
      passive: "Pasif",
      graduated: "Mezun",
    };
    return map[status] ?? status;
  }
  return status;
}

export function CustomerRecordsModal({ customer, initialKind, onClose }: Props) {
  const [kind, setKind] = useState<CustomerRecordKind>(initialKind);
  const [items, setItems] = useState<CustomerRecordItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listCustomerRecords(customer.id, kind)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(err instanceof ApiError ? err.message : "Kayıtlar yüklenemedi.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer.id, kind]);

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-card kp-customer-records-modal">
        <div className="kp-customer-records-modal__head">
          <div>
            <h3>{customer.name}</h3>
            <p className="kp-muted">Salt okunur · müşteri kurum kayıtları</p>
          </div>
          <button type="button" className="kp-btn kp-btn--ghost" onClick={onClose}>
            Kapat
          </button>
        </div>

        <div className="kp-customer-records-modal__tabs" role="tablist">
          {TABS.map((tab) => {
            const count = Number(customer[tab.countKey] ?? 0);
            return (
              <button
                key={tab.kind}
                type="button"
                role="tab"
                aria-selected={kind === tab.kind}
                className={`kp-customer-records-modal__tab${
                  kind === tab.kind ? " kp-customer-records-modal__tab--active" : ""
                }`}
                onClick={() => setKind(tab.kind)}
              >
                {tab.label} <strong>{count}</strong>
              </button>
            );
          })}
        </div>

        {error && <div className="kp-customer-records-modal__error">{error}</div>}

        <div className="kp-customer-records-modal__body">
          {loading ? (
            <p className="kp-muted">{UI_LOADING}</p>
          ) : items.length === 0 ? (
            <p className="kp-muted">Bu kategoride kayıt yok.</p>
          ) : (
            <>
              <p className="kp-customer-records-modal__meta">{total} kayıt listeleniyor</p>
              <table className="kp-customer-records-modal__table">
                <thead>
                  <tr>
                    <th>Kayıt</th>
                    <th>Detay</th>
                    <th>Durum / Rol</th>
                    <th>Şube</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.title}</strong>
                      </td>
                      <td className="kp-muted">{item.subtitle || "—"}</td>
                      <td>{statusLabel(kind, item.status)}</td>
                      <td className="kp-muted">{item.branch_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
